import { FilterMenu, FilterMenuBody } from "@faire/slate/components/filter";
import { useEffect, useState, type ReactNode } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ADS_MANAGER_STACK_ROW_CLASS } from "../constants/layout";
import { COPY } from "../copy";
import {
  computeChartYAxisMax,
  getAllStoreTypeChartData,
  getChartYAxisFormatter,
  type ChartLineConfig,
} from "../insightsMockData";
import type {
  InsightsChartMetric,
  InsightsCustomerSegment,
  InsightsStoreTypeGrouping,
} from "../types";

import { useCardPaddingClass, useIsCompact } from "./CardDensity";
import { InsightsFilterMenu } from "./InsightsFilterMenu";
import { InsightsSectionTitle } from "./InsightsSectionTitle";
import { PerformanceChartTooltip } from "./PerformanceChartTooltip";

type PerformanceByStoreTypeChartProps = {
  hasBoosting: boolean;
  selectedStoreTypes: string[];
  /** Owned by the Customers tab filter, which scopes every card on the page. */
  customerSegment: InsightsCustomerSegment;
  storeTypeGrouping: InsightsStoreTypeGrouping;
  chartMetric: InsightsChartMetric;
  onChartMetricChange: (value: InsightsChartMetric) => void;
  /**
   * Set while this card has the full page width. Only the plot's height floor
   * cares: at that width the standing floor is a letterbox.
   */
  expanded?: boolean;
  /** Control rendered opposite the title, e.g. the cumulative-view toggle. */
  headerAction?: ReactNode;
};

const METRIC_OPTIONS = [
  { value: "impressions" as const, label: "Impressions" },
  { value: "clicks" as const, label: "Clicks" },
  { value: "conversions" as const, label: "Conversions" },
  { value: "ad-sales" as const, label: "Immediate sales" },
];

/** Number of top store types selected in the legend by default. */
const DEFAULT_SELECTED_STORE_TYPE_COUNT = 5;
const MONTH_WINDOW_SIZE = 12;

function MonthNavChevron({ direction }: { direction: "left" | "right" }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d={
          direction === "left"
            ? "M9.5 3.5 5 8l4.5 4.5"
            : "M6.5 3.5 11 8l-4.5 4.5"
        }
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Alphabetical, with "Other" always pinned last. */
function sortAlphabeticalOtherLast(keys: string[]): string[] {
  return [...keys].sort((left, right) => {
    if (left === "Other") return 1;
    if (right === "Other") return -1;
    return left.localeCompare(right);
  });
}

const X_AXIS_TICK_TOP_PADDING = 8;

type MonthAxisTickProps = {
  x?: number;
  y?: number;
  payload?: { value: string };
};

function MonthAxisTick({ x, y, payload }: MonthAxisTickProps) {
  if (x == null || y == null || !payload) {
    return null;
  }

  const [monthName = "", year = ""] = payload.value.trim().split(/\s+/);

  return (
    <g transform={`translate(${x},${y + X_AXIS_TICK_TOP_PADDING})`}>
      <text textAnchor="middle" fill="#757575" fontSize={11}>
        <tspan x={0} dy={0}>
          {monthName}
        </tspan>
        <tspan x={0} dy={13}>
          {year}
        </tspan>
      </text>
    </g>
  );
}

type SquareDotProps = {
  cx?: number;
  cy?: number;
  fill?: string;
};

const PLOT_DOT_SIZE = 6;

function SquarePlotDot({ cx = 0, cy = 0, fill = "#333" }: SquareDotProps) {
  const half = PLOT_DOT_SIZE / 2;
  return (
    <rect
      x={cx - half}
      y={cy - half}
      width={PLOT_DOT_SIZE}
      height={PLOT_DOT_SIZE}
      rx={1}
      ry={1}
      fill={fill}
    />
  );
}

const CHART_TITLE_TOOLTIP = COPY.customers.storeTypeChart.titleTooltip;

/** A one-click selection, sat above the per-store-type toggles. */
type StoreTypeShortcut = {
  label: string;
  onSelect: () => void;
  /** Shown but inert, for a shortcut with nothing to select yet. */
  disabled?: boolean;
  /** Why it's inert, surfaced on hover. */
  disabledReason?: string;
};

/**
 * The legend, collapsed into a filter menu so the chart isn't fronted by a
 * dozen store-type swatches before anyone asks for them. The swatches still
 * carry the colour mapping, and the chart tooltip repeats it per line on hover,
 * so nothing is only knowable from an expanded legend.
 *
 * Rows are listed alphabetically rather than by metric rank: ranking reshuffles
 * as you toggle, which would move items out from under the cursor mid-click.
 */
function StoreTypeLegendFilter({
  legendKeys,
  lineByKey,
  selectedKeys,
  shortcuts,
  onToggle,
}: {
  legendKeys: string[];
  lineByKey: Map<string, ChartLineConfig>;
  selectedKeys: Set<string>;
  shortcuts: StoreTypeShortcut[];
  onToggle: (key: string) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const selectedCount = legendKeys.filter((key) =>
    selectedKeys.has(key)
  ).length;
  const triggerLabel = `Store types: ${
    selectedCount === legendKeys.length ? "All" : selectedCount
  }`;

  return (
    <FilterMenu
      label={triggerLabel}
      open={menuOpen}
      onClick={() => setMenuOpen(!menuOpen)}
      onClose={() => setMenuOpen(false)}
    >
      <FilterMenuBody title="Filter by store type">
        {shortcuts.length > 0 ? (
          <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-[#dfe0e1] pb-2">
            {shortcuts.map((shortcut) => (
              <button
                key={shortcut.label}
                type="button"
                onClick={shortcut.onSelect}
                disabled={shortcut.disabled}
                title={shortcut.disabled ? shortcut.disabledReason : undefined}
                className={
                  shortcut.disabled
                    ? "cursor-not-allowed text-sm whitespace-nowrap text-[#a8a8a8]"
                    : "text-sm whitespace-nowrap text-[#333] underline"
                }
              >
                {shortcut.label}
              </button>
            ))}
          </div>
        ) : null}
        <div className="flex flex-col gap-1">
          {legendKeys.map((key) => {
            const line = lineByKey.get(key);
            if (!line) {
              return null;
            }
            const isSelected = selectedKeys.has(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => onToggle(key)}
                aria-pressed={isSelected}
                className="flex w-full items-center gap-2 rounded px-1 py-1 text-left whitespace-nowrap hover:bg-[#f5f6f7]"
              >
                {isSelected ? (
                  <span
                    className="size-3 shrink-0 rounded-[3px]"
                    style={{ backgroundColor: line.color }}
                  />
                ) : (
                  <span className="bg-fs-surface-primary size-3 shrink-0 rounded-[3px] border border-[#c9cbcd]" />
                )}
                <span
                  className={
                    isSelected
                      ? "text-sm text-[#333]"
                      : "text-sm text-[#757575]"
                  }
                >
                  {line.label}
                </span>
              </button>
            );
          })}
        </div>
      </FilterMenuBody>
    </FilterMenu>
  );
}

const TOOLTIP_ESTIMATED_WIDTH = 240;
const TOOLTIP_ESTIMATED_HEIGHT = 200;

export function PerformanceByStoreTypeChart({
  hasBoosting,
  selectedStoreTypes,
  customerSegment,
  chartMetric,
  onChartMetricChange,
  expanded = false,
  headerAction,
}: PerformanceByStoreTypeChartProps) {
  const cardPaddingClass = useCardPaddingClass();
  const isCompact = useIsCompact();
  const { data, lines, rankedKeys } = getAllStoreTypeChartData(
    customerSegment,
    chartMetric
  );

  // Default selection: the top-5 store types by the initial metric. Selection
  // is user-controlled after mount and persists across metric/segment changes.
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(
    () =>
      new Set(
        getAllStoreTypeChartData(customerSegment, chartMetric).rankedKeys.slice(
          0,
          DEFAULT_SELECTED_STORE_TYPE_COUNT
        )
      )
  );
  // Zero is the latest 12-month window. Each arrow moves the window one month,
  // preserving context rather than jumping a full year at a time.
  const [monthOffset, setMonthOffset] = useState(0);

  const toggleStoreType = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const boostedStoreTypes = hasBoosting ? selectedStoreTypes : [];

  // Shortcuts, so getting back to a useful set doesn't mean toggling a dozen
  // rows by hand. "Top" follows the metric filter beside it rather than a fixed
  // list, so it re-ranks when you change what the chart plots. "Boosted" is
  // driven by the boosting props rather than a literal, so it stays disabled
  // while store-type boosting is off and lights up on its own if it returns.
  const shortcuts: StoreTypeShortcut[] = [
    {
      label: "Top store types",
      onSelect: () =>
        setSelectedKeys(
          new Set(rankedKeys.slice(0, DEFAULT_SELECTED_STORE_TYPE_COUNT))
        ),
    },
    {
      label: "Boosted store types",
      onSelect: () => setSelectedKeys(new Set(boostedStoreTypes)),
      disabled: boostedStoreTypes.length === 0,
      disabledReason: COPY.customers.storeTypeChart.boostedDisabled,
    },
  ];

  const lineByKey = new Map(lines.map((line) => [line.key, line]));

  // The filter lists every store type in a stable order; the plot and its
  // tooltip keep metric rank, so the busiest lines still read first.
  const legendKeys = sortAlphabeticalOtherLast(lines.map((line) => line.key));
  const selectedOrdered = rankedKeys.filter((key) => selectedKeys.has(key));
  const selectedLines = selectedOrdered
    .map((key) => lineByKey.get(key))
    .filter((line): line is ChartLineConfig => line != null);

  const maxMonthOffset = Math.max(0, data.length - MONTH_WINDOW_SIZE);
  const boundedMonthOffset = Math.min(monthOffset, maxMonthOffset);
  const visibleEnd = data.length - boundedMonthOffset;
  const visibleData = data.slice(
    Math.max(0, visibleEnd - MONTH_WINDOW_SIZE),
    visibleEnd
  );
  const yAxisMax = computeChartYAxisMax(visibleData, selectedOrdered);
  const yAxisFormatter = getChartYAxisFormatter(chartMetric);

  useEffect(() => {
    setMonthOffset((current) => Math.min(current, maxMonthOffset));
  }, [maxMonthOffset]);

  return (
    // `flex-1` lets this card absorb whatever height the cumulative table
    // beside it has left over, so the two columns end on the same line. The
    // table's rows are sized by their own content, so the rail can't be tuned
    // to match it by row count alone.
    <div
      className={`bg-fs-surface-primary flex flex-1 flex-col rounded-lg border border-[#dfe0e1] ${cardPaddingClass}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <InsightsSectionTitle tooltip={CHART_TITLE_TOOLTIP}>
          {COPY.customers.storeTypeChart.title}
        </InsightsSectionTitle>
        {headerAction}
      </div>
      <div className={`mt-4 ${ADS_MANAGER_STACK_ROW_CLASS}`}>
        <div className="w-full min-[733px]:w-auto">
          <InsightsFilterMenu
            name="insights-chart-metric"
            menuTitle="Filter by metric"
            labelPrefix="Metric"
            value={chartMetric}
            options={METRIC_OPTIONS}
            onChange={onChartMetricChange}
          />
        </div>
        <div className="w-full min-[733px]:w-auto">
          <StoreTypeLegendFilter
            legendKeys={legendKeys}
            lineByKey={lineByKey}
            selectedKeys={selectedKeys}
            shortcuts={shortcuts}
            onToggle={toggleStoreType}
          />
        </div>
      </div>
      {/* The legend lives in the filter row above, so the plot takes every pixel
          under the filters. It grows to whatever height the card is stretched to
          by the taller card beside it, with a floor for when this card is on its
          own and sized by its own content — raised while expanded, since at full
          page width the standing floor plots a very wide, very short chart. */}
      <div
        className={`mt-6 w-full min-w-0 ${
          expanded ? "h-[440px]" : isCompact ? "h-[186px]" : "h-[288px]"
        }`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={visibleData}
            // V3 drops the bottom margin: the axis band below already carries
            // the two-line month labels, so the margin only showed up as a gap
            // between the years and the month arrows under the card.
            margin={{ top: 8, right: 16, left: 0, bottom: isCompact ? 0 : 8 }}
          >
            <CartesianGrid stroke="#eef0f2" vertical={false} />
            <XAxis
              dataKey="month"
              type="category"
              scale="point"
              padding={{ left: 20, right: 20 }}
              tick={(props) => <MonthAxisTick {...props} />}
              tickLine={false}
              axisLine={{ stroke: "#dfe0e1" }}
              // Twelve months at this card's width crowd the two-line ticks;
              // every other month keeps the labels readable without dropping
              // points from the lines.
              interval={1}
              // Two 11px lines plus the tick's own 8px top padding need ~32px;
              // the taller band V1/V2 reserve left dead space under the years.
              height={isCompact ? 32 : 44}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#757575" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={yAxisFormatter}
              domain={[0, yAxisMax]}
              width={40}
              tickMargin={4}
            />
            <RechartsTooltip
              cursor={{ stroke: "#dfe0e1", strokeWidth: 1 }}
              offset={0}
              position={({ coordinate, viewBox }) => {
                if (!coordinate || !viewBox) {
                  return coordinate;
                }

                let x = coordinate.x + 16;
                if (x + TOOLTIP_ESTIMATED_WIDTH > viewBox.x + viewBox.width) {
                  x = coordinate.x - TOOLTIP_ESTIMATED_WIDTH - 16;
                }

                const y = Math.max(
                  viewBox.y + 8,
                  coordinate.y - TOOLTIP_ESTIMATED_HEIGHT - 12
                );

                return { x, y };
              }}
              wrapperStyle={{
                outline: "none",
                zIndex: 20,
                pointerEvents: "none",
              }}
              contentStyle={{
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
                padding: 0,
              }}
              content={(props) => (
                <PerformanceChartTooltip
                  {...props}
                  lines={selectedLines}
                  chartMetric={chartMetric}
                />
              )}
            />
            {selectedLines.map((line: ChartLineConfig) => (
              <Line
                key={line.key}
                type="linear"
                dataKey={line.key}
                stroke={line.color}
                strokeWidth={1.5}
                dot={(props) => <SquarePlotDot {...props} fill={line.color} />}
                activeDot={(props) => (
                  <SquarePlotDot {...props} fill={line.color} />
                )}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 flex justify-center gap-2">
        <button
          type="button"
          aria-label="Previous months"
          disabled={boundedMonthOffset >= maxMonthOffset}
          onClick={() =>
            setMonthOffset((current) => Math.min(maxMonthOffset, current + 1))
          }
          className="inline-flex size-8 items-center justify-center rounded border border-[#dfe0e1] text-[#333] disabled:bg-[#f7f7f7] disabled:text-[#b3b3b3]"
        >
          <MonthNavChevron direction="left" />
        </button>
        <button
          type="button"
          aria-label="Next months"
          disabled={boundedMonthOffset === 0}
          onClick={() => setMonthOffset((current) => Math.max(0, current - 1))}
          className="inline-flex size-8 items-center justify-center rounded border border-[#dfe0e1] text-[#333] disabled:bg-[#f7f7f7] disabled:text-[#b3b3b3]"
        >
          <MonthNavChevron direction="right" />
        </button>
      </div>
    </div>
  );
}
